import { useId, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { Input, type InputProps } from "@/shared/components/ui/Input";
import { Label } from "@/shared/components/ui/Label";

/**
 * Text-input form field wrapper (design.md §10).
 *
 * The mx-4 on label/error is REQUIRED: Input carries px-4 of inner padding,
 * and the mx-4 here MUST line up with the input's text content rather than
 * with its outer rounded edge. Stripping this is out of spec unless the
 * underlying input padding is also restyled.
 */
export interface FormFieldProps extends InputProps {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
}

export function FormField({
  label,
  hint,
  error,
  id,
  className,
  ...inputProps
}: FormFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={inputId} className="mx-4">
        {label}
      </Label>
      <Input id={inputId} aria-invalid={error ? true : undefined} {...inputProps} />
      {error ? (
        <p className="mx-4 type-body-sm text-danger-strong">{error}</p>
      ) : hint ? (
        <p className="mx-4 type-body-sm text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
